/**
 * LocalStorage Keys
 * Centralized constants for localStorage keys used across the application
 */
export const STORAGE_KEYS = {
  // v2: bumped to reset every existing user's stored preference so the sidebar
  // starts collapsed by default for everyone; users can still toggle it after.
  SIDEBAR_COLLAPSED: 'sidebar-collapsed-v2',
  LIBRARY_ASSETS_VIEW_MODE: 'library-assets-view-mode',
  /** Unsent prompt typed in the new-draft composer, persisted across navigation. */
  DRAFTS_NEW_PROMPT: 'drafts-unsent-prompt-new',
  /**
   * Single unsent editor draft prompt (the last composer the user typed in),
   * stored as `{ scopeId, message }`. Only one is kept at a time, so opening a
   * different draft never resurfaces another's text.
   */
  DRAFTS_EDITOR_PROMPT: 'drafts-unsent-prompt-editor',
  /**
   * Pending "selected preview text" context snippets for the editor composer —
   * the "Select" bubble-menu action adds a snippet here; the chat renders them
   * as removable chips and folds them into the next message. Scoped like the
   * prompt slot so only the owning composer surfaces them.
   */
  DRAFTS_EDITOR_SELECTION_CONTEXT: 'drafts-selection-context-editor',
  /** Whether the draft editor's right rail is collapsed to its icon strip. */
  DRAFT_RAIL_COLLAPSED: 'draft-rail-collapsed',
  /** Whether the asset workspace's config rail is expanded past its icon strip. */
  ASSET_CONFIG_RAIL_EXPANDED: 'asset-config-rail-expanded',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * Stage values for jeeves run filters (`ListRuns` `stage` field). PascalCase —
 * the enum names ("AUTOMATION_STAGE_*") and grimoire's UPPERCASE form are rejected here.
 */
export const RUN_STAGE = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
} as const;

export type RunStage = (typeof RUN_STAGE)[keyof typeof RUN_STAGE];

/**
 * State values for jeeves run filters (`ListRuns` `runState` field). These map
 * to the API's runState enum. Valid values: Created, Pending, Executing,
 * Stopped, NeedsWakeup, WaitingForTimer, NeedsGuidance, WaitingForGuidance,
 * WaitingForBook, Completed. (There is no `Failed` runState.)
 */
export const RUN_STATE = {
  COMPLETED: 'Completed',
  STOPPED: 'Stopped',
} as const;

/**
 * Terminal run states — a run in any of these is finished, not active. The API
 * runState enum has no `Failed` value, so terminal is Completed + Stopped.
 * Note: filter these with `!=` per state; the API rejects `NOT (...)`.
 */
export const TERMINAL_RUN_STATES = [
  RUN_STATE.COMPLETED,
  RUN_STATE.STOPPED,
] as const;

export type RunStateFilter = (typeof RUN_STATE)[keyof typeof RUN_STATE];
